import { Col, Row, Form, Button, Spinner, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useState } from 'react'
import { ClaimFileDto, ClaimFormDto, LoadDocumentFollowUpReplyDto } from "../../types";
import { loadDocumentFolloupReply } from "../../api/ClaimFollowUpAPI";
import AlertMessage from "../../../../components/AlertMessage";
import { AlertType } from "../../../../hooks/useAlert";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import ClaimAttachDocumentsModal from "../modals/ClaimAttachDocumentsModal";
import SelectedFilterChip from "../../../../components/Filters/components/SelectedFilterChip";

interface EditClaimFollowUpReplyEmailProps {
    claim: ClaimFormDto;
    professionalId: number;
    professionalName: string;
    setReplyEmail: React.Dispatch<React.SetStateAction<boolean>>;
    setMessageSuccess: (message: string) => void;
    onSaved: () => void;
}

export default function EditClaimFollowUpReplyEmail({
    claim, professionalId, professionalName, setReplyEmail, setMessageSuccess, onSaved
}: EditClaimFollowUpReplyEmailProps) {
    const { t } = useTranslation(['claim', 'common']);
    const [loading, setLoading] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertType, setAlertType] = useState<AlertType>('success');
    const [alertMessage, setAlertMessage] = useState('');
    const [showAttachDocumentsModal, setShowAttachDocumentsModal] = useState(false);

    const [errors, setErrors] = useState<{ message?: string }>({});
    const [formData, setFormData] = useState<LoadDocumentFollowUpReplyDto>({
        claimId: claim.id,
        message: "",
        professionalId: professionalId
    });

    const handleSaveReplyEmail = async () => {
        const newErrors: { message?: string } = {};

        if (!formData.message.trim()) {
            newErrors.message = t("common:form.requiredFieldForm");
        }
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            setLoading(true);
            await loadDocumentFolloupReply(formData);
            setLoading(false);
            setReplyEmail(false);
            setMessageSuccess(t("claim:detail.messageSendMessageSuccessfully"))
            onSaved();
        } catch (e) {
            setLoading(false); // Reactiva el botón
            setAlertType('danger');
            let errorMessage =
                (e as ApiErrorResponse)?.message || t("common:genericFormErrors.unexpectedErorr");

            setAlertMessage(errorMessage);
            setShowAlert(true);
        }
    };

    const handleRemoveFile = (indexToRemove: number) => {
        const nextFiles = (formData.files || []).filter((_, index) => index !== indexToRemove);
        setFormData((prev) => ({
            ...prev,
            files: nextFiles.length > 0 ? nextFiles : undefined
        }));
    };

    const handleRemoveClaimFile = (indexToRemove: number) => {
        const nextFiles = (formData.claimFiles || []).filter((_, index) => index !== indexToRemove);
        setFormData((prev) => ({
            ...prev,
            claimFiles: nextFiles.length > 0 ? nextFiles : undefined,
        }));
    };
    
    const addAttachedDocuments = (data: { selectedClaimFiles?: ClaimFileDto[]; addedFiles?: File[] }) => {
        setFormData((prev) => {
            return {
                ...prev,
                files: [...(data.addedFiles || [])],
                claimFiles: [...(data.selectedClaimFiles || [])]
            };
        });
    }

    return (
        <div className="active-section">
            <Row className="w-100">
                <ClaimAttachDocumentsModal
                    claimId={claim.id}
                    show={showAttachDocumentsModal}
                    possibleClaimFiles={claim.files}
                    onClose={() => setShowAttachDocumentsModal(false)}
                    onSubmit={addAttachedDocuments}
                />
                {loading && (
                    <div className="overlay-spinner">
                        <Spinner animation="border" variant="light" />
                    </div>
                )}
                {showAlert && (
                    <AlertMessage
                        message={alertMessage}
                        type={alertType}
                    />
                )}
                <Row className="h-90 justify-content-center">
                    <Col md={9} className="d-flex flex-column justify-content-center align-items-center">
                        <div className="mb-3 w-100" style={{ maxWidth: '360px' }}>
                            <img
                                src="/logo-craiteria.svg"
                                alt="Craiteria"
                                style={{ width: '100%' }}
                            />
                        </div>

                        <h4 className="mb-3 text-center">
                            {t("claim:followUpResonse.title")}
                        </h4>
                    </Col>
                </Row>
                <Row className="mb-3 ml-2">
                        {t("claim:followUpReply.title", { name: professionalName ?? "" })}
                </Row>
                <Row>
                    <Form.Group>
                        <Form.Control
                            as="textarea"               
                            rows={8}   
                            value={formData.message}
                            isInvalid={!!errors.message}
                            onChange={(e) => {
                                setFormData((prev) => {
                                    return {
                                        ...prev,
                                        message: e.target.value
                                    };
                                })
                            }}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.message}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Row>
                <Row className="mt-2">
                    <Col md={10}>
                        {((formData.files || []).length > 0 || (formData.claimFiles || []).length > 0) &&
                            <Stack direction="horizontal" gap={2} className="d-flex align-items-center gap-2 flex-wrap">
                                <span><strong>{t("claim:followUpComment.fileListAttachment")}</strong></span>
                                {(formData.files || []).map((file, index) => {
                                    return (
                                        <SelectedFilterChip
                                            key={`${file.name}-${index}`}
                                            label=""
                                            value={file.name}
                                            onRemove={() =>
                                                handleRemoveFile(index)
                                            }
                                        />
                                    );
                                })}
                                {(formData.claimFiles || []).map((file, index) => {
                                    return (
                                        <SelectedFilterChip
                                            key={`${file.fileName}-${index}`}
                                            label=""
                                            value={file.fileName}
                                            onRemove={() =>
                                                handleRemoveClaimFile(index)
                                            }
                                        />
                                    );
                                })}
                            </Stack>
                        }
                    </Col>
                    <Col md={2} className="d-flex justify-content-end align-items-start">
                        <a href="#" className="ms-auto" onClick={() => setShowAttachDocumentsModal(true)}>
                            <p className="text-secondary mb-0">
                                {t("claim:followUpReply.buttonAttachment")}
                            </p>
                        </a>
                    </Col>
                </Row>
                <Row className="mt-5">
                    <Col  className="text-end">
                        <Button variant="secondary" onClick={() => setReplyEmail(false)}>
                            {t("common:buttons.buttonCancel")}
                        </Button>                        
                        <Button variant="primary" className="ms-2" onClick={() => handleSaveReplyEmail()}>
                            {t("claim:followUpReply.buttonSaveAnswer")}
                        </Button>                        
                    </Col>
                </Row>
            </Row>
        </div>
    );
}


